import { FC, InputHTMLAttributes } from 'react';
import { IMaskInput, IMaskInputProps } from 'react-imask';

import { getInputAria } from './Input.helpers';
import { InputBase } from './Input.type';
import { FieldWrapper } from './atoms/FieldWrapper';

/** Keys to block when integerOnly is true (no minus, scientific notation, or decimal) */
const INTEGER_ONLY_BLOCKED_KEYS = new Set(['-', 'e', 'E', '.']);

type Props = Prettify<
  InputBase<string> &
    Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'ref'> & {
      mask:
        | string
        | {
            type: 'number';
            scale?: number;
            /** When true, only positive integers; blocks -, e and . */
            integerOnly?: boolean;
          };
      onChange: (value: string) => void;
    }
>;

const parseNumber = (value: number | string | undefined) => {
  return value ? Number.parseInt(value.toString()) : undefined;
};

export const InputMasked: FC<Props> = ({
  className,
  inputClassName,
  mask,
  onChange,
  label,
  placeholder,
  labelOutside,
  value,
  min,
  max,
  ...props
}) => {
  const isNumberMask = typeof mask !== 'string';
  const integerOnly = isNumberMask && mask.integerOnly;

  // Build mask props conditionally based on mask type
  const maskProps: IMaskInputProps<HTMLInputElement> =
    typeof mask === 'string'
      ? {
          mask: mask,
        }
      : {
          mask: Number,
          min: parseNumber(min) ?? (integerOnly ? 0 : undefined),
          max: parseNumber(max),
          thousandsSeparator: ',',
          radix: '.',
          scale: integerOnly ? 0 : mask.scale,
        };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (integerOnly && INTEGER_ONLY_BLOCKED_KEYS.has(e.key)) {
      e.preventDefault();
    }
    props.onKeyDown?.(e);
  };

  return (
    <FieldWrapper {...props} label={label} className={className} labelOutside={labelOutside}>
      <IMaskInput
        {...props}
        {...getInputAria(props)}
        {...maskProps}
        type={integerOnly ? 'number' : props.type}
        onKeyDown={handleKeyDown}
        defaultValue={value}
        onAccept={(value: string) => onChange(value)}
        className={inputClassName}
        placeholder={placeholder ?? ' '} // placeholder must to be " " when nullish to avoid ios styling issues
        data-testid={props['data-testid']}
      />
    </FieldWrapper>
  );
};
